import React, { useState } from 'react';
import {
  AlertTriangle,
  Lightbulb,
  Calendar,
  Activity,
  Pill,
  FileText,
  Bookmark,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  ExternalLink,
} from 'lucide-react';
import { useAIHealthCompanionStore } from '@/stores/ai-health-companion-store';
import { cn } from '@/lib/utils';
import { GlassCard } from '@/components/ui/glass-card';
import PipelineStageIndicator from './PipelineStageIndicator';
import EmergencySafetyBanner from './EmergencySafetyBanner';
import type { AIHealthMessage, AIResponseSection } from '@/types/ai-health-companion-types';

interface StreamingMessageBubbleProps {
  message: AIHealthMessage;
}

const StreamingMessageBubble: React.FC<StreamingMessageBubbleProps> = ({ message }) => {
  const { bookmarkMessage, unbookmarkMessage, bookmarkedMessageIds, retryLastMessage } =
    useAIHealthCompanionStore();

  const [showCitations, setShowCitations] = useState(false);

  const isBookmarked = bookmarkedMessageIds.includes(message.id);

  const handleBookmarkToggle = () => {
    if (isBookmarked) {
      unbookmarkMessage(message.id);
    } else {
      bookmarkMessage(message.id);
    }
  };

  const renderSectionIcon = (type: AIResponseSection['type']) => {
    switch (type) {
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400" />;
      case 'recommendation':
        return <Lightbulb className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
      case 'screening-reminder':
        return <Calendar className="w-5 h-5 text-purple-600 dark:text-purple-400" />;
      case 'medication-info':
        return <Pill className="w-5 h-5 text-green-600 dark:text-green-400" />;
      case 'test-explanation':
        return <Activity className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />;
      default:
        return <FileText className="w-5 h-5 text-gray-600 dark:text-gray-400" />;
    }
  };

  const renderSection = (section: AIResponseSection, index: number) => {
    const borderColors: Record<AIResponseSection['type'], string> = {
      warning: 'border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950/30',
      recommendation: 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30',
      'screening-reminder':
        'border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-950/30',
      'medication-info':
        'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30',
      'test-explanation':
        'border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-950/30',
      summary: 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30',
      detail: 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30',
      'metric-card': 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900',
    };

    if (section.type === 'metric-card') {
      return (
        <GlassCard key={index} variant="subtle" padding="md" className="mt-3">
          <div className="flex items-start gap-3">
            <Activity className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {section.title}
              </h4>
              {section.content && (
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                  {section.content}
                </p>
              )}
            </div>
          </div>
        </GlassCard>
      );
    }

    return (
      <div
        key={index}
        className={cn(
          'rounded-lg border p-3 mt-3',
          borderColors[section.type] || borderColors.detail
        )}
      >
        <div className="flex items-start gap-2.5">
          {renderSectionIcon(section.type)}
          <div className="flex-1 min-w-0">
            {section.title && (
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
                {section.title}
              </h4>
            )}
            {section.content && (
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {section.content}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const displayContent = message.isStreaming
    ? message.streamedContent || ''
    : message.content;

  const showPipeline =
    message.pipelineStatus &&
    message.pipelineStatus.currentStage !== 'complete' &&
    !message.pipelineStatus.currentStage.includes('generating');

  return (
    <div className="space-y-2">
      {/* Emergency Banner */}
      {message.emergencyDetected && (
        <EmergencySafetyBanner safetyDisclaimer={message.safetyDisclaimer} />
      )}

      {/* Main Message Bubble */}
      <div className="relative">
        <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl rounded-tl-sm px-4 py-3">
          {/* Bookmark Button */}
          <button
            onClick={handleBookmarkToggle}
            className={cn(
              'absolute top-2 right-2 p-1.5 rounded-lg transition-colors',
              isBookmarked
                ? 'text-primary-600 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/30'
                : 'text-gray-400 dark:text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700'
            )}
            aria-label={isBookmarked ? 'Remove bookmark' : 'Bookmark message'}
          >
            <Bookmark className={cn('w-4 h-4', isBookmarked && 'fill-current')} />
          </button>

          {/* Pipeline Status */}
          {showPipeline && message.pipelineStatus && (
            <div className="mb-3 pr-8">
              <PipelineStageIndicator status={message.pipelineStatus} />
            </div>
          )}

          {/* Message Content */}
          {displayContent && (
            <div className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words pr-8">
              {displayContent}
              {message.isStreaming && (
                <span className="inline-block w-0.5 h-4 bg-primary-600 dark:bg-primary-400 ml-0.5 animate-pulse">
                  |
                </span>
              )}
            </div>
          )}

          {/* Confidence Score */}
          {message.confidenceScore !== undefined && (
            <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 bg-white dark:bg-gray-900 rounded-md text-xs">
              <span className="text-gray-600 dark:text-gray-400">Confidence:</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {Math.round(message.confidenceScore * 100)}%
              </span>
            </div>
          )}

          {/* Structured Sections */}
          {message.sections && message.sections.map((section, idx) => renderSection(section, idx))}

          {/* Error State */}
          {message.error && (
            <div className="mt-3 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-red-700 dark:text-red-300">{message.error}</p>
                  <button
                    onClick={retryLastMessage}
                    className="mt-2 inline-flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium"
                  >
                    <RotateCcw className="w-3 h-3" />
                    Retry
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          {message.actions && message.actions.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {message.actions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    // Handle action - could be passed via store or callback
                    console.log('Action clicked:', action);
                  }}
                  className={cn(
                    'px-3 py-1.5 text-sm font-medium rounded-lg transition-colors',
                    action.priority === 'high'
                      ? 'bg-primary-600 hover:bg-primary-700 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100'
                  )}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}

          {/* Citations */}
          {message.citations && message.citations.length > 0 && (
            <div className="mt-3">
              <button
                onClick={() => setShowCitations(!showCitations)}
                className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 font-medium"
              >
                {showCitations ? (
                  <ChevronUp className="w-3 h-3" />
                ) : (
                  <ChevronDown className="w-3 h-3" />
                )}
                Sources ({message.citations.length})
              </button>

              {showCitations && (
                <div className="mt-2 space-y-1.5">
                  {message.citations.map((citation, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-2 p-2 bg-white dark:bg-gray-900 rounded-md"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-900 dark:text-gray-100">
                          {citation.title}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {citation.source}
                        </p>
                        {citation.relevanceScore !== undefined && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                            Relevance: {Math.round(citation.relevanceScore * 100)}%
                          </p>
                        )}
                      </div>
                      {citation.url && (
                        <a
                          href={citation.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                          aria-label="Open citation"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Timestamp */}
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {new Date(message.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      </div>
    </div>
  );
};

export default React.memo(StreamingMessageBubble);
